import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { Search, Mic, Camera, Square, Loader2, X, Sparkles, Zap, RefreshCw, SwitchCamera, Users } from "lucide-react";
import { ProductCard } from "./HomePage";
import { Link } from "react-router-dom";
import SearchFilters from "../components/SearchFilters";
import { useVoiceSearch } from "../lib/useVoiceSearch";
import { useAuth } from "../lib/AuthContext";

export default function SearchPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [camOpen, setCamOpen] = useState(false);
  const [shopper, setShopper] = useState(null); // {owner, owner_name, filters}
  const voice = useVoiceSearch();

  // Derive which filter keys came from the active shopper (intersection of filters & shopper.filters)
  const autoApplied = React.useMemo(() => {
    if (!shopper?.filters) return {};
    const arrEq = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => x === b[i]);
    const out = {};
    for (const [k, v] of Object.entries(shopper.filters)) {
      const cur = filters[k];
      if (Array.isArray(v) ? arrEq(cur, v) : cur === v) out[k] = v;
    }
    return out;
  }, [filters, shopper]);

  // Fetch active shopper's preferences and merge into empty filter slots
  const loadActive = async (catId) => {
    if (!user) { setShopper(null); return; }
    try {
      const params = catId ? `?category_id=${catId}` : "";
      const { data } = await api.get(`/buyer/active-filters${params}`);
      setShopper(data);
      if (Object.keys(data.filters || {}).length === 0) return;
      // Apply only to empty slots; don't overwrite explicit user input
      setFilters(prev => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(data.filters)) {
          if (next[k] == null || next[k] === "") next[k] = v;
        }
        return next;
      });
    } catch (e) {
      // Filters endpoint optional; default UI still works without it.
      if (process.env.NODE_ENV !== "production") console.warn("[search] active filters fetch:", e?.message);
    }
  };

  // Single effect — fetches on user / category change
  useEffect(() => {
    loadActive(filters.category_id);
    // eslint-disable-next-line
  }, [user, filters.category_id]);

  const resetShopperFilters = () => {
    setFilters(prev => {
      const next = { ...prev };
      for (const k of Object.keys(autoApplied)) {
        if (next[k] === autoApplied[k]) delete next[k];
      }
      return next;
    });
    // shopper stays so user can re-apply via category change
  };

  // When voice produces a transcript, run the search automatically
  useEffect(() => {
    if (voice.transcript && !voice.recording) {
      setQ(voice.transcript);
      runSearch({ query: voice.transcript });
    }
    // eslint-disable-next-line
  }, [voice.transcript, voice.recording]);

  const runSearch = async (override = {}) => {
    const query = override.query !== undefined ? override.query : q;
    const f = override.filters !== undefined ? override.filters : filters;
    setLoading(true);
    try {
      const { data } = await api.post("/search", { query, ...f });
      setResult(data);
    } finally { setLoading(false); }
  };

  // re-run when filters change (debounced via simple effect)
  useEffect(() => {
    const t = setTimeout(() => {
      if (q || Object.values(filters).some(v => v !== "" && v != null && v !== false)) {
        runSearch();
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const textSearch = async (query = q) => {
    setQ(query);
    await runSearch({ query });
  };

  const handleVisualResult = (data) => {
    setKeywords(data.keywords || "");
    setQ(data.query || "");
    setResult(data);
  };

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mb-6">Search<span className="text-[#D4FF00]">.</span></h1>

      <div className="flex gap-2 items-center bg-[#261534] border border-[#3D2845] rounded-full p-2 pl-4 mb-3">
        <Search size={18} className="text-[#A1A1AA]"/>
        <input
          data-testid="search-input"
          value={q}
          onChange={e=>setQ(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&runSearch()}
          placeholder="Search products, stores, live…"
          className="bg-transparent outline-none flex-1 text-white"
        />
        <button
          data-testid="search-voice-btn"
          onClick={voice.recording ? voice.stop : voice.start}
          disabled={voice.busy}
          title={voice.supported ? "Voice search (Web Speech)" : "Voice search (Whisper)"}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 ${
            voice.recording ? "bg-[#FF007F] text-white animate-pulse" : "bg-[#3D2845] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black"
          }`}>
          {voice.busy ? <Loader2 size={16} className="animate-spin"/> :
           voice.recording ? <Square size={14} className="fill-white"/> : <Mic size={18}/>}
        </button>
        <button
          data-testid="search-photo-btn"
          onClick={()=>setCamOpen(true)}
          className="w-11 h-11 rounded-full bg-[#3D2845] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center transition active:scale-95">
          <Camera size={18}/>
        </button>
        <button data-testid="search-submit-btn" onClick={()=>runSearch()} className="btn-primary text-xs px-4 py-2">Go</button>
      </div>

      {/* Active shopper banner — auto-applied from preferences */}
      {shopper?.owner_name && Object.keys(autoApplied).length > 0 && (
        <div className="mb-3 px-4 py-3 rounded-2xl bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center gap-2 flex-wrap"
             role="status" aria-live="polite" data-testid="shopper-banner">
          <Users size={14} className="text-[#D4FF00]"/>
          <div className="text-xs">
            <span className="text-[#A1A1AA]">Showing results for </span>
            <span className="font-bold text-[#D4FF00]">{shopper.owner_name}</span>
            {shopper.owner_relation && <span className="text-[#A1A1AA]"> ({shopper.owner_relation})</span>}
            <span className="text-[#A1A1AA]"> · </span>
            <span className="text-[#FAFAFA]">
              {Object.entries(autoApplied).map(([k, v]) => {
                const labelMap = { size: "Size", sizes: "Sizes", gender: "Gender", genders: "Genders" };
                const label = labelMap[k] || k.replace(/_/g, " ");
                let display;
                if (Array.isArray(v)) display = v.join(", ");
                else if (k === "gender") display = String(v).charAt(0).toUpperCase() + String(v).slice(1);
                else display = v;
                return `${label} ${display}`;
              }).join(" · ")}
            </span>
          </div>
          <button onClick={resetShopperFilters} data-testid="shopper-reset-btn"
            className="ml-auto chip text-xs">
            <X size={10}/> Show all
          </button>
          <Link to="/profile" className="chip text-xs" data-testid="shopper-switch-link">
            <Sparkles size={10}/> Switch
          </Link>
        </div>
      )}

      {/* Live voice indicator */}
      {(voice.recording || voice.interim || voice.error) && (
        <div className={`mb-3 px-4 py-3 rounded-2xl border ${voice.error ? "border-[#FF007F]/40 bg-[#FF007F]/5" : "border-[#FF007F]/40 bg-[#FF007F]/5"}`}
             data-testid="voice-indicator">
          <div className="flex items-center gap-2">
            {voice.recording && <span className="w-2 h-2 rounded-full bg-[#FF007F] animate-pulse"/>}
            <span className="text-xs uppercase tracking-widest font-bold text-[#FF007F]">
              {voice.recording ? `Listening… (${voice.via === "whisper" ? "recording" : "live"})`
               : voice.busy ? "Transcribing…" : voice.error ? "Error" : "Heard"}
            </span>
          </div>
          {voice.interim && <div className="text-sm text-[#A1A1AA] italic mt-1">{voice.interim}</div>}
          {voice.transcript && !voice.recording && (
            <div className="text-sm text-[#FAFAFA] mt-1" data-testid="voice-transcript">
              "{voice.transcript}"
            </div>
          )}
          {voice.error && <div className="text-sm text-[#FF007F] mt-1">{voice.error}</div>}
        </div>
      )}

      <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mb-4">
        <span className="text-[#D4FF00]">●</span> text
        <span className="text-[#FF007F]">●</span> voice {voice.supported && <span className="text-[10px] text-[#A1A1AA]">(real-time)</span>}
        <span className="text-[#00F0FF]">●</span> live camera (point at item)
      </div>

      {/* Filters */}
      <div className="mb-4">
        <SearchFilters value={filters} onChange={setFilters}/>
      </div>

      {/* Quick suggestions */}
      <div className="flex gap-2 flex-wrap mb-8">
        {["Shoes", "Beauty", "Hoodie", "Headphones", "Skincare", "Jewelry"].map(s => (
          <button key={s}
            onClick={()=>textSearch(s)}
            data-testid={`search-quick-${s.toLowerCase()}`}
            className="chip">
            {s}
          </button>
        ))}
      </div>

      {keywords && (
        <div className="mb-6 text-sm">
          <span className="text-[#A1A1AA]">AI detected: </span>
          <span className="text-[#00F0FF] font-mono-display">{keywords}</span>
        </div>
      )}

      {loading && <div className="text-center text-[#A1A1AA] py-8">Searching…</div>}

      {result && <SearchResults result={result}/>}

      {camOpen && <VisualSearchModal onClose={()=>setCamOpen(false)} onResult={handleVisualResult}/>}
    </div>
  );
}

function SearchResults({ result }) {
  const empty = ["products","stores","streams","campaigns","videos"].every(k => (result[k]||[]).length === 0);
  return (
    <div className="space-y-10">
      {result.streams?.length > 0 && (
        <section>
          <h3 className="font-display text-xl font-bold uppercase mb-3">Live streams</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {result.streams.map(s => (
              <Link key={s.stream_id} to={`/live/${s.stream_id}`} className="card"><div className="aspect-video"><img src={s.thumbnail_url} className="w-full h-full object-cover"/></div><div className="p-3 text-sm font-bold">{s.title}</div></Link>
            ))}
          </div>
        </section>
      )}
      {result.stores?.length > 0 && (
        <section>
          <h3 className="font-display text-xl font-bold uppercase mb-3">Stores</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {result.stores.map(s => (
              <Link key={s.store_id} to={`/store/${s.store_id}`} className="card"><div className="h-24"><img src={s.banner_url} className="w-full h-full object-cover"/></div><div className="p-3 text-sm font-bold">{s.name}</div></Link>
            ))}
          </div>
        </section>
      )}
      {result.products?.length > 0 && (
        <section>
          <h3 className="font-display text-xl font-bold uppercase mb-3">Products</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {result.products.map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
          </div>
        </section>
      )}
      {result.campaigns?.length > 0 && (
        <section>
          <h3 className="font-display text-xl font-bold uppercase mb-3">Campaigns</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {result.campaigns.map(c => (
              <Link key={c.campaign_id} to={`/campaign/${c.campaign_id}`} className="card"><div className="h-32"><img src={c.banner_url} className="w-full h-full object-cover"/></div><div className="p-3 font-bold">{c.title}</div></Link>
            ))}
          </div>
        </section>
      )}
      {empty && (
        <div className="text-center text-[#A1A1AA] py-16">No results. Try another query.</div>
      )}
    </div>
  );
}

/* --------------------------------------------------------- */
/* Visual Search — live camera viewfinder */
function VisualSearchModal({ onClose, onResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | live | analyzing | done | error
  const [front, setFront] = useState(false); // back-camera default for product scanning
  const [resultPreview, setResultPreview] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");

  const start = async (useFront = front) => {
    try {
      setStatus("starting");
      // stop any existing
      streamRef.current?.getTracks().forEach(t => t.stop());
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: useFront ? "user" : { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(()=>{});
      }
      setStatus("live");
    } catch (e) {
      setError("Camera permission denied or unavailable.");
      setStatus("error");
    }
  };

  useEffect(() => { start(front); /* mount */
    return () => { streamRef.current?.getTracks().forEach(t => t.stop()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = async () => {
    const next = !front; setFront(next);
    await start(next);
  };

  const snapAndSearch = async () => {
    if (!videoRef.current) return;
    setStatus("analyzing");
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = Math.min(v.videoWidth || 720, 720);
    const aspect = (v.videoHeight || 1280) / (v.videoWidth || 720);
    canvas.height = canvas.width * aspect;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    setSnapshot(dataUrl);

    canvas.toBlob(async (blob) => {
      if (!blob) { setStatus("live"); return; }
      const form = new FormData();
      form.append("image", blob, "snap.jpg");
      try {
        const { data } = await api.post("/search/photo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setResultPreview(data);
        setStatus("done");
      } catch (e) {
        setError(e.response?.data?.detail || "Visual search failed");
        setStatus("error");
      }
    }, "image/jpeg", 0.75);
  };

  const acceptResults = () => {
    if (resultPreview) onResult(resultPreview);
    onClose();
  };

  const retry = () => {
    setResultPreview(null);
    setSnapshot(null);
    setStatus("live");
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#3D2845]">
        <button onClick={onClose} data-testid="visual-close-btn" className="w-9 h-9 rounded-full bg-[#261534] hover:bg-[#3D2845] flex items-center justify-center"><X size={18}/></button>
        <div className="font-display font-black uppercase text-sm flex items-center gap-1">
          <Sparkles size={14} className="text-[#00F0FF]"/> Visual search
        </div>
        <button onClick={flip} disabled={status==="analyzing"} data-testid="visual-flip-btn"
          className="w-9 h-9 rounded-full bg-[#261534] hover:bg-[#3D2845] flex items-center justify-center disabled:opacity-50">
          <SwitchCamera size={16}/>
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center bg-black overflow-hidden">
        {/* Live video */}
        <video ref={videoRef} playsInline muted autoPlay className="max-w-full max-h-full object-contain"/>

        {/* Targeting reticle */}
        {status === "live" && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-64 h-64 sm:w-80 sm:h-80 border-2 border-[#00F0FF] rounded-3xl relative">
              <span className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-[#D4FF00]"></span>
              <span className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-[#D4FF00]"></span>
              <span className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-[#D4FF00]"></span>
              <span className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-[#D4FF00]"></span>
            </div>
          </div>
        )}

        {/* Captured snapshot (during analyze + done) */}
        {snapshot && status !== "live" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <img src={snapshot} alt="snap" className="max-w-full max-h-full object-contain opacity-80"/>
          </div>
        )}

        {status === "starting" && (
          <div className="absolute inset-0 flex items-center justify-center text-[#A1A1AA]">
            <Loader2 size={28} className="animate-spin mr-2"/> Starting camera…
          </div>
        )}

        {status === "analyzing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
            <div className="relative w-24 h-24 mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-[#00F0FF] border-t-transparent animate-spin"/>
              <Zap size={32} className="absolute inset-0 m-auto text-[#D4FF00]"/>
            </div>
            <div className="font-display font-bold uppercase text-sm tracking-widest">AI scanning…</div>
            <div className="text-xs text-[#A1A1AA] mt-1">Detecting product</div>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="text-[#FF007F] font-display font-bold mb-2">{error || "Something went wrong"}</div>
            <button onClick={retry} className="btn-primary mt-3"><RefreshCw size={14} className="inline mr-1"/>Try again</button>
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="px-4 py-4 bg-[#0F0817] border-t border-[#3D2845]">
        {status === "live" && (
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-widest font-bold">Point at an item, then tap to scan</p>
            <button onClick={snapAndSearch} data-testid="visual-snap-btn"
              className="w-20 h-20 rounded-full bg-white flex items-center justify-center active:scale-90 transition shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-[#00F0FF] border-4 border-white flex items-center justify-center">
                <Camera size={28} className="text-black"/>
              </div>
            </button>
          </div>
        )}

        {status === "done" && resultPreview && (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Sparkles size={16} className="text-[#00F0FF] shrink-0"/>
              <div className="text-xs text-[#A1A1AA]">AI detected:</div>
              <div className="text-sm font-mono-display text-[#00F0FF] truncate">{resultPreview.keywords || "(no description)"}</div>
            </div>

            {/* Quick suggested products */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-3">
              {(resultPreview.products || []).slice(0, 8).map((p) => (
                <Link key={p.product_id} to={`/product/${p.product_id}`} onClick={onClose}
                  data-testid={`visual-suggest-${p.product_id}`}
                  className="shrink-0 w-32 card">
                  <div className="aspect-square"><img src={p.images?.[0]} className="w-full h-full object-cover"/></div>
                  <div className="p-2">
                    <div className="text-xs font-bold line-clamp-1">{p.name}</div>
                    <div className="text-xs text-[#D4FF00] font-display font-bold">{fmtMoney(p.price)}</div>
                  </div>
                </Link>
              ))}
              {(resultPreview.products || []).length === 0 && (
                <div className="text-xs text-[#A1A1AA] py-4">No matching products found — try another angle or item.</div>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={retry} data-testid="visual-retry-btn" className="btn-secondary flex-1">
                <RefreshCw size={14} className="inline mr-1"/>Scan again
              </button>
              <button onClick={acceptResults} data-testid="visual-accept-btn" className="btn-primary flex-1">
                See all matches →
              </button>
            </div>
          </div>
        )}

        {status === "starting" && (
          <div className="text-center text-xs text-[#A1A1AA] uppercase tracking-widest">Loading camera…</div>
        )}
        {status === "analyzing" && (
          <div className="text-center text-xs text-[#A1A1AA] uppercase tracking-widest">Hang tight…</div>
        )}
      </div>
    </div>
  );
}
